/**
 * Custom hook bundling every Continuity System API handler. The hook itself stays
 * small (state + reload + wiring) — individual action handlers live as
 * top-level helpers that receive the deps they need so the analyzer counts
 * them independently and the hook body stays well under the complexity
 * threshold.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const buildHeaders = (json = false) => {
  const t = localStorage.getItem('auth_token');
  const h = t ? { Authorization: `Bearer ${t}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const api = async (path, method = 'GET', body) => {
  const opts = { method, headers: buildHeaders(!!body) };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BACKEND_URL}${path}`, opts);
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try { const j = await res.json(); detail = j.detail || detail; } catch (_) { /* ignore */ }
    throw new Error(detail);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res;
};

const EMPTY_TRUSTED_CONTACT = {
  first_name: '', last_name: '', email: '', phone: '', address: '', country: '',
  relationship: '', relationship_notes: '',
  role: 'Information Only', role_other: '',
};

// ── Top-level handler helpers (each counted separately by the analyzer)

async function doActivate({ consents, state, setSubmitting, reload }) {
  if (!(consents.nature && consents.ndpa && consents.ack)) {
    toast.error('All three consents are required.');
    return;
  }
  setSubmitting(true);
  try {
    await api('/api/estate-vault/activate', 'POST', {
      consent_nature_of_service: consents.nature,
      consent_ndpa_privacy: consents.ndpa,
      consent_successor_acknowledgement: consents.ack,
    });
    toast.success('Continuity System is on. You can add up to ' + (state?.max_successors || 3) + ' Trusted Contacts.');
    await reload();
  } catch (e) {
    toast.error(e.message);
  } finally {
    setSubmitting(false);
  }
}

async function doAddTrustedContact({ e, newContact, setNewContact, setSubmitting, reload }) {
  e.preventDefault();
  setSubmitting(true);
  try {
    const payload = { ...newContact };
    // Only send role_other when role === "Other"
    if (payload.role !== 'Other') payload.role_other = undefined;
    await api('/api/estate-vault/successors', 'POST', payload);
    toast.success('Trusted Contact added. They will only be contacted if our check-in cascade ever completes.');
    setNewContact(EMPTY_TRUSTED_CONTACT);
    await reload();
  } catch (err) {
    toast.error(err.message);
  } finally {
    setSubmitting(false);
  }
}

function doPreview() {
  const token = localStorage.getItem('auth_token');
  if (!token) { toast.error('Please sign in again.'); return; }
  fetch(`${BACKEND_URL}/api/estate-vault/snapshot/latest/preview`, { headers: buildHeaders() })
    .then(async (r) => {
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Could not preview snapshot');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
    })
    .catch((e) => toast.error(e.message));
}

async function doGenerateSnapshot({ setSubmitting, setSnapshots }) {
  setSubmitting(true);
  try {
    await api('/api/estate-vault/snapshot/generate', 'POST');
    toast.success('Continuity Records refreshed. Opening preview…');
    const snaps = await api('/api/estate-vault/snapshots');
    setSnapshots(snaps.snapshots || []);
    setTimeout(() => doPreview(), 300);
  } catch (e) {
    toast.error(e.message);
  } finally {
    setSubmitting(false);
  }
}

async function simpleAction({ endpoint, successMsg, reload }) {
  try { await api(endpoint, 'POST'); toast.success(successMsg); await reload(); }
  catch (e) { toast.error(e.message); }
}

async function doCheckinRespond({ reload }) {
  try {
    const list = await api('/api/estate-vault/checkins');
    const rows = list?.checkins || [];
    const pending = rows.find((c) => !c.response && !c.responded_at) || rows[0];
    if (!pending?.checkin_id) {
      toast.error('No pending check-in found to respond to.');
      return;
    }
    await api('/api/estate-vault/checkin/respond', 'POST', {
      checkin_id: pending.checkin_id,
      response: 'i_am_well',
    });
    toast.success("Thanks — we've reset your check-in clock. All good.");
    await reload();
  } catch (e) {
    toast.error(e.message);
  }
}

// ── Public hook

export function useEstateVaultActions() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [consents, setConsents] = useState({ nature: false, ndpa: false, ack: false });
  const [newContact, setNewContact] = useState(EMPTY_TRUSTED_CONTACT);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [snapshots, setSnapshots] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/estate-vault');
      setState(data);
      // Also learn how many investments the user has so we can show a friendly
      // empty-state on the Portfolio Summary card for Continuity-first users.
      try {
        const me = await api('/api/auth/me');
        setState((prev) => (prev ? { ...prev, investment_count: me?.investment_count ?? 0 } : prev));
      } catch (_) { /* ignore — non-critical */ }
      if (data.exists) {
        try {
          const snaps = await api('/api/estate-vault/snapshots');
          setSnapshots(snaps.snapshots || []);
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      logger.error('estate-vault load failed', e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleRemove = async (id) => {
    try { await api(`/api/estate-vault/successors/${id}`, 'DELETE'); toast.success('Removed.'); await reload(); }
    catch (e) { toast.error(e.message); }
  };
  const handleReorder = async (id, newPriority) => {
    try {
      await api(`/api/estate-vault/successors/${id}`, 'PATCH', { priority: newPriority });
      await reload();
    } catch (e) { toast.error(e.message); }
  };
  const handleUpdateTrustedContact = async (id, changes) => {
    try {
      await api(`/api/estate-vault/successors/${id}`, 'PATCH', changes);
      toast.success('Saved.');
      await reload();
      return true;
    } catch (e) { toast.error(e.message); return false; }
  };
  const loadAudit = async () => {
    try { const a = await api('/api/estate-vault/audit-log'); setAuditRows(a.events || []); setAuditOpen(true); }
    catch (e) { toast.error(e.message); }
  };

  return {
    loading, state, submitting, consents, setConsents,
    newContact, setNewContact,
    // Back-compat aliases so callers that still read `newSucc`/`setNewSucc`
    // continue to work while the UI is being renamed.
    newSucc: newContact, setNewSucc: setNewContact,
    auditOpen, setAuditOpen, auditRows, snapshots,
    handleActivate: () => doActivate({ consents, state, setSubmitting, reload }),
    handleAddTrustedContact: (e) => doAddTrustedContact({ e, newContact, setNewContact, setSubmitting, reload }),
    // Back-compat alias.
    handleAddSuccessor: (e) => doAddTrustedContact({ e, newContact, setNewContact, setSubmitting, reload }),
    handleRemoveTrustedContact: handleRemove,
    handleRemoveSuccessor: handleRemove,
    handleReorderTrustedContact: handleReorder,
    handleUpdateTrustedContact,
    handlePreviewSuccessor: async (successor_id) => {
      try {
        const r = await api(`/api/estate-vault/successors/${successor_id}/preview-link`, 'POST');
        if (r && r.url) {
          const abs = `${window.location.origin}${r.url}`;
          window.open(abs, '_blank', 'noopener,noreferrer');
          toast.success('Preview opened. Safe to walk through — nothing is sent to your Trusted Contact.');
        }
      } catch (err) {
        toast.error(err.message || 'Could not open the preview.');
      }
    },
    handleGenerateSnapshot: () => doGenerateSnapshot({ setSubmitting, setSnapshots }),
    handlePause: () => simpleAction({ endpoint: '/api/estate-vault/pause', successMsg: 'Paused. Check-ins will not be sent until you resume.', reload }),
    handleResume: () => simpleAction({ endpoint: '/api/estate-vault/resume', successMsg: 'Resumed. Your next check-in cycle has been reset.', reload }),
    handleCheckinRespond: () => doCheckinRespond({ reload }),
    handleRevoke: () => simpleAction({ endpoint: '/api/estate-vault/revoke', successMsg: 'Continuity System is now off. Your Trusted Contacts will not receive anything.', reload }),
    handlePreview: doPreview,
    loadAudit,
    reload,
  };
}
